import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { flowRight } from 'lodash';

import { type WithTranslation } from '@wix/yoshi-flow-editor';
import withTranslate from '../../hoc/with-translate';
import { handleEnterKeyUp } from '../../services/accessibility';
import { type Actions, type AppState } from '../../types';
import { SearchIcon } from '../icons/search-icon';
import { connect } from '../runtime-context';
import ClearIcon from './clear-icon';
import styles from './search-input.scss';

type OwnProps = {
  value?: string;
  isExpanded?: boolean;
  isFullWidth?: boolean;
  onChange?: (value: string) => void;
  post?: unknown;
  inputClassName?: string;
  iconClassName?: string;
  borderClassName?: string;
  className?: string;
  type?: string;
  color?: string;
};

type Props = OwnProps &
  Partial<Pick<ReturnType<typeof mapRuntimeToProps>, 'headerSearchOpened' | 'headerSearchClosed'>> &
  Omit<ReturnType<typeof mapRuntimeToProps>, 'headerSearchOpened' | 'headerSearchClosed'> &
  Pick<WithTranslation, 't'>;

export const SearchInput: React.FC<Props> = (props) => {
  const {
    type,
    className,
    inputClassName,
    iconClassName,
    borderClassName,
    color,
    t,
    isFullWidth,
    onChange,
  } = props;
  const [value, setValue] = useState(props.value || '');
  const [isExpanded, setIsExpanded] = useState(props.isExpanded);
  const input = useRef<HTMLInputElement>(null);
  const wasExpanded = useRef(isExpanded);
  const isExpandable = !props.isExpanded;

  useEffect(() => {
    if (!wasExpanded.current && isExpanded) {
      input.current?.focus();
    }
    wasExpanded.current = isExpanded;
  }, [isExpanded]);

  const expand = () => {
    setIsExpanded(true);
    props.headerSearchOpened?.();
  };

  const collapse = () => {
    setIsExpanded(false);
    props.headerSearchClosed?.();
  };

  const goToSearchPage = () => {
    props.setIsLoading('search', '', true);
    props.searchSubmitted({
      query: value,
      post: props.post,
    });
    props.navigateToSearch(value);
  };

  const changeValue = (nextValue: string) => {
    setValue(nextValue);
    onChange?.(nextValue);
  };

  const handleClear = () => {
    changeValue('');
    if (isExpandable) {
      collapse();
    }
  };

  const handleClick = () => {
    if (isExpandable && !isExpanded) {
      expand();
    } else {
      goToSearchPage();
    }
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      goToSearchPage();
    } else if (event.key === 'Escape') {
      (event.target as HTMLInputElement).blur();
      handleClear();
    }
  };

  const showClearButton = (isExpandable && isExpanded) || (!isExpandable && value);
  const containerClassName = classNames(
    styles.container,
    className,
    'search-input',
    type && styles[type],
    {
      [styles.expanded]: isExpanded,
      [styles.isFullWidth]: isFullWidth,
    },
  );

  return (
    <div className={containerClassName} tabIndex={-1}>
      <div
        tabIndex={!isExpanded ? 0 : undefined}
        role="button"
        aria-label={t('search-input.placeholder')}
        onKeyUp={handleEnterKeyUp(handleClick)}
        onClick={handleClick}
      >
        <SearchIcon className={classNames(styles.icon, iconClassName)} fill={color} />
      </div>
      <input
        ref={input}
        className={classNames(styles.input, inputClassName, 'search-input__input')}
        onChange={(e) => changeValue(e.target.value)}
        value={value}
        type="text"
        placeholder={t('search-input.placeholder')}
        onKeyUp={handleKeyUp}
        style={{ color }}
      />
      <div className={classNames(styles.spacer, inputClassName, styles.placeholder)}>
        {t('search-input.placeholder')}
      </div>
      <div
        className={classNames(styles.border, borderClassName)}
        style={{ backgroundColor: color }}
      />
      {showClearButton ? (
        <button
          className={classNames(styles.clear, 'search-input__clear-button')}
          onClick={handleClear}
          tabIndex={-1}
          data-hook="clear-button"
        >
          <ClearIcon className={classNames(styles.clearIcon, iconClassName)} fill={color} />
        </button>
      ) : null}
    </div>
  );
};

const mapRuntimeToProps = (_state: AppState, _ownProps: {}, actions: Actions) => ({
  headerSearchOpened: actions.headerSearchOpened,
  headerSearchClosed: actions.headerSearchClosed,
  navigateToSearch: actions.navigateToSearch,
  setIsLoading: actions.setIsLoading,
  searchSubmitted: actions.searchSubmitted,
});

export default flowRight(connect(mapRuntimeToProps), withTranslate)(SearchInput);
